
import { useState, useEffect } from 'react';
import { Project } from '@/types/project';
import { Client, Invoice, TimeEntry } from '@/utils/mockData';

export const useDemoData = () => {
  const demoClients: Client[] = [
    {
      id: 'client-1',
      name: 'Acme Corporation',
      company: 'Acme Inc.',
      email: 'contact@acmecorp.com',
      phone: '(555) 123-4567',
      avatar: '',
      totalProjects: 4,
      totalInvoiced: 12500,
      status: 'active',
      lastContact: '2023-12-15',
      notes: 'Key client for Q1 projects',
      type: 'customer',
      website: 'https://acmecorp.example.com'
    },
    {
      id: 'client-2',
      name: 'TechStart Solutions',
      company: 'TechStart LLC',
      email: 'info@techstart.co',
      phone: '(555) 987-6543',
      avatar: '',
      totalProjects: 2,
      totalInvoiced: 8750,
      status: 'active',
      lastContact: '2023-12-10',
      type: 'customer',
      website: 'https://techstart.example.com'
    },
    {
      id: 'client-3',
      name: 'Global Innovations',
      company: 'Global Inc.',
      email: 'contact@globalinc.com',
      phone: '(555) 456-7890',
      avatar: '',
      totalProjects: 1,
      totalInvoiced: 5000,
      status: 'prospect',
      lastContact: '2023-12-05',
      notes: 'Interested in services, follow up needed',
      type: 'customer'
    },
    {
      id: 'client-4',
      name: 'Bright Designs',
      company: 'Bright Studios',
      email: 'hello@brightstudios.design',
      phone: '(555) 234-5678',
      avatar: '',
      totalProjects: 3,
      totalInvoiced: 15000,
      status: 'active',
      lastContact: '2023-12-12',
      type: 'customer',
      website: 'https://brightstudios.example.com'
    }
  ];
  
  const demoProjects: Project[] = [
    {
      id: 'project-1',
      clientId: 'client-1',
      title: 'Website Redesign',
      description: 'Complete redesign of company website with new branding and improved user experience.',
      startDate: '2023-11-15',
      dueDate: '2024-01-15',
      status: 'ongoing',
      progress: 65,
      tasks: {
        completed: 8,
        total: 12,
        items: [
          { id: 'task-1-1', title: 'Design mockups', completed: true, createdAt: '2023-11-16', projectId: 'project-1' },
          { id: 'task-1-2', title: 'Frontend development', completed: true, createdAt: '2023-11-20', projectId: 'project-1' },
          { id: 'task-1-3', title: 'Backend integration', completed: false, createdAt: '2023-12-01', projectId: 'project-1' },
          { id: 'task-1-4', title: 'Content migration', completed: false, createdAt: '2023-12-10', projectId: 'project-1' }
        ]
      },
      hourlyRate: 85,
      totalHours: 120,
      tasksCompleted: 8,
      tasksTotal: 12,
      // Adding the previously missing properties
      logged_hours: 62,
      remaining_hours: 58,
      hours_percentage: 52,
      due_date: '2024-01-15',
      taskItems: [
        { id: 'task-1-1', title: 'Design mockups', completed: true, createdAt: '2023-11-16', projectId: 'project-1' },
        { id: 'task-1-2', title: 'Frontend development', completed: true, createdAt: '2023-11-20', projectId: 'project-1' },
        { id: 'task-1-3', title: 'Backend integration', completed: false, createdAt: '2023-12-01', projectId: 'project-1' },
        { id: 'task-1-4', title: 'Content migration', completed: false, createdAt: '2023-12-10', projectId: 'project-1' }
      ],
      tasks_completed: 8,
      tasks_total: 12
    },
    {
      id: 'project-2',
      clientId: 'client-2',
      title: 'Mobile App Development',
      description: 'Development of a cross-platform mobile application for customer engagement.',
      startDate: '2023-12-01',
      dueDate: '2024-03-01',
      status: 'ongoing',
      progress: 30,
      tasks: {
        completed: 3,
        total: 10,
        items: [
          { id: 'task-2-1', title: 'UX research', completed: true, createdAt: '2023-12-02', projectId: 'project-2' },
          { id: 'task-2-2', title: 'UI design', completed: true, createdAt: '2023-12-05', projectId: 'project-2' },
          { id: 'task-2-3', title: 'Frontend development', completed: false, createdAt: '2023-12-15', projectId: 'project-2' }
        ]
      },
      hourlyRate: 95,
      totalHours: 200,
      tasksCompleted: 3,
      tasksTotal: 10,
      // Adding the previously missing properties
      logged_hours: 45,
      remaining_hours: 155,
      hours_percentage: 22.5,
      due_date: '2024-03-01',
      taskItems: [
        { id: 'task-2-1', title: 'UX research', completed: true, createdAt: '2023-12-02', projectId: 'project-2' },
        { id: 'task-2-2', title: 'UI design', completed: true, createdAt: '2023-12-05', projectId: 'project-2' },
        { id: 'task-2-3', title: 'Frontend development', completed: false, createdAt: '2023-12-15', projectId: 'project-2' }
      ],
      tasks_completed: 3,
      tasks_total: 10
    },
    {
      id: 'project-3',
      clientId: 'client-4',
      title: 'Brand Identity Creation',
      description: 'Development of comprehensive brand identity including logo, color palette, and style guide.',
      startDate: '2023-10-15',
      dueDate: '2023-12-15',
      status: 'completed',
      progress: 100,
      tasks: {
        completed: 6,
        total: 6,
        items: [
          { id: 'task-3-1', title: 'Market research', completed: true, createdAt: '2023-10-16', projectId: 'project-3' },
          { id: 'task-3-2', title: 'Logo design', completed: true, createdAt: '2023-10-25', projectId: 'project-3' },
          { id: 'task-3-3', title: 'Style guide creation', completed: true, createdAt: '2023-11-10', projectId: 'project-3' }
        ]
      },
      hourlyRate: 75,
      totalHours: 80,
      tasksCompleted: 6,
      tasksTotal: 6,
      // Adding the previously missing properties
      logged_hours: 80,
      remaining_hours: 0,
      hours_percentage: 100,
      due_date: '2023-12-15',
      taskItems: [
        { id: 'task-3-1', title: 'Market research', completed: true, createdAt: '2023-10-16', projectId: 'project-3' },
        { id: 'task-3-2', title: 'Logo design', completed: true, createdAt: '2023-10-25', projectId: 'project-3' },
        { id: 'task-3-3', title: 'Style guide creation', completed: true, createdAt: '2023-11-10', projectId: 'project-3' }
      ],
      tasks_completed: 6,
      tasks_total: 6
    },
    {
      id: 'project-4',
      clientId: 'client-1',
      title: 'E-commerce Integration',
      description: 'Integration of e-commerce functionality into existing website.',
      startDate: '2023-12-10',
      dueDate: '2024-02-10',
      status: 'planning',
      progress: 10,
      tasks: {
        completed: 1,
        total: 8,
        items: [
          { id: 'task-4-1', title: 'Requirements gathering', completed: true, createdAt: '2023-12-11', projectId: 'project-4' },
          { id: 'task-4-2', title: 'Platform selection', completed: false, createdAt: '2023-12-15', projectId: 'project-4' }
        ]
      },
      hourlyRate: 90,
      totalHours: 100,
      tasksCompleted: 1,
      tasksTotal: 8,
      // Adding the previously missing properties
      logged_hours: 8,
      remaining_hours: 92,
      hours_percentage: 8,
      due_date: '2024-02-10',
      taskItems: [
        { id: 'task-4-1', title: 'Requirements gathering', completed: true, createdAt: '2023-12-11', projectId: 'project-4' },
        { id: 'task-4-2', title: 'Platform selection', completed: false, createdAt: '2023-12-15', projectId: 'project-4' }
      ],
      tasks_completed: 1,
      tasks_total: 8
    }
  ];
  
  const demoInvoices: Invoice[] = [
    {
      id: 'invoice-1',
      clientId: 'client-1',
      projectId: 'project-1',
      number: 'INV-2023-001',
      issuedDate: '2023-11-30',
      dueDate: '2023-12-14',
      amount: 4250,
      status: 'paid',
      items: [
        { id: 'item-1-1', description: 'Website design - initial phase', hours: 25, rate: 85, amount: 2125 },
        { id: 'item-1-2', description: 'Frontend development', hours: 25, rate: 85, amount: 2125 }
      ]
    },
    {
      id: 'invoice-2',
      clientId: 'client-2',
      projectId: 'project-2',
      number: 'INV-2023-002',
      issuedDate: '2023-12-15',
      dueDate: '2023-12-29',
      amount: 3800,
      status: 'pending',
      items: [
        { id: 'item-2-1', description: 'Mobile app UX research', hours: 20, rate: 95, amount: 1900 },
        { id: 'item-2-2', description: 'UI design and prototyping', hours: 20, rate: 95, amount: 1900 }
      ]
    },
    {
      id: 'invoice-3',
      clientId: 'client-4',
      projectId: 'project-3',
      number: 'INV-2023-003',
      issuedDate: '2023-12-15',
      dueDate: '2023-12-29',
      amount: 6000,
      status: 'paid',
      items: [
        { id: 'item-3-1', description: 'Brand identity complete package', hours: 80, rate: 75, amount: 6000 }
      ]
    },
    {
      id: 'invoice-4',
      clientId: 'client-1',
      projectId: 'project-1',
      number: 'INV-2024-001',
      issuedDate: '2024-01-05',
      dueDate: '2024-01-19',
      amount: 2550,
      status: 'draft',
      items: [
        { id: 'item-4-1', description: 'Backend integration', hours: 30, rate: 85, amount: 2550 }
      ]
    }
  ];
  
  const demoTimeEntries: TimeEntry[] = [
    {
      id: 'time-1',
      projectId: 'project-1',
      description: 'Website mockup design',
      date: '2023-12-10',
      hours: 4.5,
      billable: true
    },
    {
      id: 'time-2',
      projectId: 'project-1',
      description: 'Frontend development - homepage',
      date: '2023-12-11',
      hours: 6,
      billable: true
    },
    {
      id: 'time-3',
      projectId: 'project-2',
      description: 'App UI wireframes',
      date: '2023-12-12',
      hours: 3.5,
      billable: true
    },
    {
      id: 'time-4',
      projectId: 'project-2',
      description: 'Client meeting - requirements review',
      date: '2023-12-13',
      hours: 1.5,
      billable: true
    },
    {
      id: 'time-5',
      projectId: 'project-1',
      description: 'Backend API integration',
      date: '2023-12-14',
      hours: 5,
      billable: true
    }
  ];

  return {
    isDemoMode: false,
    demoClients,
    demoProjects,
    demoInvoices,
    demoTimeEntries
  };
};

export default useDemoData;
